import { appLocale } from "./i18n";

export type LegalDocumentType = "privacy" | "terms";

export interface LegalSection {
  title: string;
  paragraphs: string[];
}

export interface LegalDocument {
  title: string;
  subtitle: string;
  effectiveDate: string;
  intro: string[];
  sections: LegalSection[];
}

export function getLegalDocument(type: LegalDocumentType): LegalDocument {
  const locale = appLocale === "zh-CN" ? "zh-CN" : "en-US";
  return legalDocuments[locale][type];
}

const legalDocuments: Record<"zh-CN" | "en-US", Record<LegalDocumentType, LegalDocument>> = {
  "zh-CN": {
    privacy: {
      title: "隐私政策",
      subtitle: "适用于 Autumn Pet 写实桌宠生成台及配套桌面客户端。",
      effectiveDate: "生效日期：2026 年 6 月 14 日",
      intro: [
        "Autumn Pet 由 Autumn Soft 提供。我们希望用清晰、容易理解的方式说明：当你上传宠物照片、生成动作、购买额度、绑定桌宠或使用登录功能时，我们会如何处理相关信息。",
        "本政策适用于中国版网站、后端服务和桌宠客户端。若你不同意本政策，请停止使用相关服务。"
      ],
      sections: [
        {
          title: "1. 我们收集的信息",
          paragraphs: [
            "账号信息：邮箱密码登录信息、微信登录产生的公开资料（如昵称、头像、开放平台标识）、登录状态、用户专属 ID。",
            "宠物资料：你上传的宠物照片、平台抠图结果、宠物名称、宠物对你的称呼、宠物动作配置、生成的视频、抽帧后的桌宠皮肤包和配对码。",
            "生成与订单信息：所选动作、高级动作描述、生成任务状态、供应商任务 ID、生成额度、宠物位、支付订单、支付渠道、退款记录和额度流水。",
            "设备与日志信息：浏览器类型、访问时间、接口错误日志、桌宠客户端版本、绑定状态、下载记录以及保障服务稳定所需的安全日志。"
          ]
        },
        {
          title: "2. 我们如何使用信息",
          paragraphs: [
            "为你创建和管理账号、宠物资料、生成额度和宠物位。",
            "处理宠物照片，包括压缩、抠图、生成动作视频、抽帧、打包桌宠皮肤并同步到本地桌宠客户端。",
            "完成登录、支付、退款、订单查询、客服排查、异常恢复、安全风控和防止滥用。",
            "在不识别具体个人的前提下，统计功能使用情况、改进生成质量和产品体验。"
          ]
        },
        {
          title: "3. AI 生成与第三方服务",
          paragraphs: [
            "为了生成写实桌宠动作，我们可能会把经过处理的宠物图片、动作描述和必要任务参数发送给已配置的 AI 生成供应商。当前中国版使用即梦/火山引擎相关能力。",
            "支付能力可能由微信支付或其他我们在页面中明确展示的支付服务提供方处理。支付服务方会根据其规则处理支付所需信息。",
            "我们不会主动出售你的宠物照片、生成视频或账号信息。除非为提供服务、遵守法律、处理争议或获得你的授权，我们不会向无关第三方披露你的个人信息。"
          ]
        },
        {
          title: "4. 信息保存与删除",
          paragraphs: [
            "账号、订单、额度流水等信息会在你使用服务期间保存，并在法律法规要求的期限内留存。",
            "宠物照片、抠图、生成视频和桌宠皮肤包会为了预览、同步、重新下载和问题排查保存。你可以联系我们请求删除不再需要的宠物资料。",
            "当你注销账号或请求删除信息时，我们会在合理期限内处理；法律法规要求留存或技术上暂无法删除的，我们会停止除保存和安全保护之外的处理。"
          ]
        },
        {
          title: "5. 你的权利",
          paragraphs: [
            "你可以请求访问、更正、复制、删除你的个人信息，或撤回同意、注销账号、查询订单和额度变动。",
            "你可以停止使用高级动作描述、第三方登录或桌宠绑定功能。部分信息是提供核心服务所必需的，删除后可能导致生成、同步或售后能力不可用。",
            "如需行使上述权利，请通过本政策末尾的联系方式联系我们。"
          ]
        },
        {
          title: "6. 未成年人",
          paragraphs: [
            "Autumn Pet 面向具备完全民事行为能力的用户。未满 14 周岁的未成年人请不要自行使用本服务；如确需使用，应由监护人阅读并同意本政策。"
          ]
        },
        {
          title: "7. 安全措施",
          paragraphs: [
            "我们会采用 HTTPS、访问控制、日志审计、令牌认证、权限隔离、文件路径校验等措施保护信息安全。",
            "互联网服务无法保证绝对安全。如果发生可能影响你权益的安全事件，我们会按法律法规要求采取补救措施并进行通知。"
          ]
        },
        {
          title: "8. 政策更新与联系我们",
          paragraphs: [
            "我们可能会根据功能、供应商、法律法规或运营需要更新本政策。重大变更会在页面上提示。",
            "隐私相关问题可联系：support@autumnsoft.net。"
          ]
        }
      ]
    },
    terms: {
      title: "服务条款",
      subtitle: "适用于 Autumn Pet 写实桌宠生成台及配套桌面客户端。",
      effectiveDate: "生效日期：2026 年 6 月 14 日",
      intro: [
        "欢迎使用 Autumn Pet。使用本服务即表示你理解并同意本服务条款。若你不同意，请停止使用。",
        "本服务仍在持续迭代，AI 生成、桌宠同步、支付和客户端能力可能随版本变化。"
      ],
      sections: [
        {
          title: "1. 服务内容",
          paragraphs: [
            "Autumn Pet 支持上传宠物照片、生成写实宠物动作视频、抽帧打包桌宠皮肤、通过配对码同步到本地桌面客户端。",
            "服务可能包含默认动作、额外动作、高级动作描述、重新生成、购买生成额度、购买宠物位、桌宠客户端下载和绑定。"
          ]
        },
        {
          title: "2. 账号与安全",
          paragraphs: [
            "你需要使用邮箱密码、微信登录或我们支持的其他登录方式使用服务。你应保证账号信息真实、合法，并妥善保管登录凭据。",
            "因你主动泄露账号、共享设备或使用不安全环境造成的损失，由你自行承担。发现异常使用时，请及时联系我们。"
          ]
        },
        {
          title: "3. 用户内容与授权",
          paragraphs: [
            "你应确保上传的宠物照片、动作描述和其他内容由你合法拥有或已获得必要授权，不侵犯他人肖像、隐私、著作权、商标权或其他权益。",
            "为提供服务，你授予我们在必要范围内处理、存储、抠图、生成、转换、展示、下载和同步相关内容的权利。该授权仅用于向你提供 Autumn Pet 服务、故障排查和安全维护。"
          ]
        },
        {
          title: "4. 使用限制",
          paragraphs: [
            "不得上传违法、侵权、恶意、恐怖、血腥、虐待动物、诈骗、骚扰、侵犯隐私或不适合生成的内容。",
            "不得绕过额度、批量滥用接口、攻击系统、抓取非公开数据、反向工程客户端、干扰他人正常使用或利用生成结果从事违法活动。",
            "我们可以对违规内容、异常账号或高风险请求采取拒绝生成、限制功能、暂停账号、删除内容或配合法律要求的措施。"
          ]
        },
        {
          title: "5. 额度、付款与退款",
          paragraphs: [
            "生成额度和宠物位用于发起对应服务请求。点击生成或重新生成后，后端会根据当前动作和高级描述扣除额度；实际扣费、退款和补偿以后台记录为准。",
            "付费商品、价格、币种、可购买类型和支付渠道以页面展示为准。中国版当前优先使用微信支付。",
            "如发生退款，已发放且未使用的额度或宠物位会被扣回；若相关权益已使用，可能无法直接退款，需要人工处理。"
          ]
        },
        {
          title: "6. AI 结果与可用性",
          paragraphs: [
            "AI 生成结果具有不确定性，可能出现宠物外观偏差、动作不自然、背景瑕疵或生成失败。我们会提供预览、重新生成和错误提示来改善体验，但不保证每次结果完全符合预期。",
            "因模型供应商、网络、浏览器、操作系统、桌面客户端版本、第三方支付或不可抗力造成的中断，我们会尽力恢复，但不承诺服务永不中断。"
          ]
        },
        {
          title: "7. 知识产权",
          paragraphs: [
            "Autumn Pet 的产品界面、代码、品牌、图标、文案、流程和服务能力归 Autumn Soft 或相关权利人所有。",
            "你上传的原始内容归你或原权利人所有。生成结果在法律允许范围内可由你用于个人桌宠体验；商业使用前请自行确认素材授权、平台规则和适用法律。"
          ]
        },
        {
          title: "8. 责任限制与条款更新",
          paragraphs: [
            "在法律允许范围内，我们不对间接损失、数据丢失、商业损失、第三方服务故障或你违反本条款造成的损失承担责任。",
            "我们可能更新本条款。重大变更会在页面提示；继续使用服务视为接受更新后的条款。",
            "服务相关问题可联系：support@autumnsoft.net。"
          ]
        }
      ]
    }
  },
  "en-US": {
    privacy: {
      title: "Privacy Policy",
      subtitle: "For Autumn Pet realistic desktop pet studio and the companion desktop client.",
      effectiveDate: "Effective date: June 14, 2026",
      intro: [
        "Autumn Pet is provided by Autumn Soft. This policy explains, in plain language, how we handle information when you upload pet photos, generate actions, buy credits, pair the desktop client, or sign in.",
        "This policy applies to the international website, backend services, and desktop client. If you do not agree with it, please do not use the service."
      ],
      sections: [
        {
          title: "1. Information we collect",
          paragraphs: [
            "Account information: Google sign-in profile data such as name, avatar, provider identifier, account ID, and session state. If password sign-in is available, we process the email address and password credentials required for sign-in.",
            "Pet content: pet photos you upload, cutout images, pet names, what your pet calls you, selected actions, generated videos, extracted frames, desktop skin packages, and pairing codes.",
            "Generation and payment information: selected actions, advanced action descriptions, generation job status, provider task IDs, credit balances, pet slots, payment orders, payment channel, refunds, and quota transactions.",
            "Device and log information: browser type, access time, API error logs, desktop client version, pairing state, download records, and security logs needed to keep the service reliable."
          ]
        },
        {
          title: "2. How we use information",
          paragraphs: [
            "To create and manage your account, pet profiles, credits, pet slots, and order records.",
            "To process pet photos, including compression, background removal, action video generation, frame extraction, desktop skin packaging, and desktop client sync.",
            "To support sign-in, payments, refunds, order lookup, support, abuse prevention, security, and service recovery.",
            "To understand feature usage and improve generation quality in an aggregated or non-identifying way."
          ]
        },
        {
          title: "3. AI generation and service providers",
          paragraphs: [
            "To generate realistic pet actions, we may send processed pet images, action descriptions, and task parameters to configured AI generation providers.",
            "Payments may be handled by Stripe or another payment provider shown at checkout. Payment providers process payment information under their own terms and privacy practices.",
            "We do not sell your pet photos, generated videos, or account information. We disclose information only when needed to provide the service, comply with law, resolve disputes, protect safety, or with your permission."
          ]
        },
        {
          title: "4. Retention and deletion",
          paragraphs: [
            "Account, order, credit, and quota transaction records are kept while you use the service and for legally required retention periods.",
            "Pet photos, cutouts, generated videos, and skin packages may be stored for preview, sync, redownload, troubleshooting, and support. You may ask us to delete pet content you no longer need.",
            "When you request deletion or account closure, we will handle it within a reasonable period. If law requires retention or deletion is technically limited, we will restrict further processing except storage and security protection."
          ]
        },
        {
          title: "5. Your choices and rights",
          paragraphs: [
            "Depending on your location, you may have rights to access, correct, copy, delete, restrict, or object to certain processing of your personal information.",
            "You can stop using advanced action descriptions, third-party sign-in, or desktop pairing. Some information is necessary for core generation and sync features, so deleting it may make those features unavailable.",
            "To make a privacy request, contact us using the address at the end of this policy."
          ]
        },
        {
          title: "6. Children",
          paragraphs: [
            "Autumn Pet is not directed to children under 13, or a higher age where local law requires parental consent. Children should not use the service without a parent or guardian."
          ]
        },
        {
          title: "7. Security",
          paragraphs: [
            "We use HTTPS, access controls, audit logs, token authentication, permission separation, file path validation, and other safeguards to protect information.",
            "No internet service is perfectly secure. If a security incident may affect your rights, we will take appropriate steps and provide notice where required by law."
          ]
        },
        {
          title: "8. Updates and contact",
          paragraphs: [
            "We may update this policy when features, providers, laws, or operations change. Material changes will be shown in the product or on this page.",
            "Privacy contact: support@autumnsoft.net."
          ]
        }
      ]
    },
    terms: {
      title: "Terms of Service",
      subtitle: "For Autumn Pet realistic desktop pet studio and the companion desktop client.",
      effectiveDate: "Effective date: June 14, 2026",
      intro: [
        "Welcome to Autumn Pet. By using the service, you understand and agree to these Terms of Service. If you do not agree, please do not use the service.",
        "The service is still evolving. AI generation, desktop sync, payment, and client features may change as new versions are released."
      ],
      sections: [
        {
          title: "1. The service",
          paragraphs: [
            "Autumn Pet lets you upload pet photos, generate realistic pet action videos, extract frames, package desktop pet skins, and sync them to the local desktop client with a pairing code.",
            "Features may include default actions, extra actions, advanced action descriptions, regeneration, credit purchases, pet slot purchases, desktop client downloads, and desktop pairing."
          ]
        },
        {
          title: "2. Accounts and security",
          paragraphs: [
            "You may need to sign in with Google, email/password, or another supported provider. You are responsible for keeping your login credentials and devices secure.",
            "You must provide accurate account information and notify us if you believe your account has been used without permission."
          ]
        },
        {
          title: "3. Your content and license",
          paragraphs: [
            "You are responsible for ensuring that you have the rights to upload pet photos, action descriptions, and other content, and that your content does not violate privacy, publicity, copyright, trademark, or other rights.",
            "You grant us a limited license to process, store, remove backgrounds from, generate, transform, display, download, and sync your content as needed to provide Autumn Pet, troubleshoot issues, and maintain security."
          ]
        },
        {
          title: "4. Acceptable use",
          paragraphs: [
            "Do not upload illegal, infringing, malicious, abusive, graphic, animal cruelty, deceptive, harassing, privacy-invasive, or otherwise unsuitable content.",
            "Do not bypass credits, abuse APIs, attack the system, scrape private data, reverse engineer the client, disrupt other users, or use generated results for illegal activity.",
            "We may refuse generation, limit features, suspend accounts, remove content, or cooperate with lawful requests when content or behavior violates these terms."
          ]
        },
        {
          title: "5. Credits, payments, and refunds",
          paragraphs: [
            "Credits and pet slots are used to request specific service operations. When you click Generate or Regenerate, the backend deducts credits based on selected actions and advanced descriptions. Backend records control final billing, refunds, and adjustments.",
            "Products, prices, currencies, purchasable items, and payment channels are shown at checkout. The international version uses Stripe where configured.",
            "If an order is refunded, unused credits or pet slots granted by that order may be removed. If benefits have already been used, a direct refund may not be available and support review may be required."
          ]
        },
        {
          title: "6. AI results and availability",
          paragraphs: [
            "AI results are probabilistic and may include appearance differences, imperfect movement, background artifacts, or failed generation. We provide preview, regeneration, and error feedback, but we do not guarantee that every result will match your expectations.",
            "Service interruptions may be caused by model providers, network conditions, browsers, operating systems, desktop client versions, payment providers, or events outside our control. We will try to restore service but do not promise uninterrupted availability."
          ]
        },
        {
          title: "7. Intellectual property",
          paragraphs: [
            "The Autumn Pet interface, code, brand, icons, copy, workflows, and service features belong to Autumn Soft or its licensors.",
            "You or the original rights holder retain ownership of the content you upload. You may use generated results for personal desktop pet experiences where lawful. For commercial use, you are responsible for checking source material rights, platform rules, and applicable law."
          ]
        },
        {
          title: "8. Liability and changes",
          paragraphs: [
            "To the fullest extent permitted by law, we are not liable for indirect losses, lost data, business losses, third-party service failures, or losses caused by your violation of these terms.",
            "We may update these terms. Material changes will be shown in the product or on this page. Continued use means you accept the updated terms.",
            "Service contact: support@autumnsoft.net."
          ]
        }
      ]
    }
  }
};
